import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import {
  devicePairingService,
  normaliseUserCode,
  type ApprovedClient,
  type DeviceVerification,
} from '../services/devicePairingService';
import './ConnectPage.css';

/**
 * `/connect` — the RFC 8628 verification page (A3).
 *
 * This is the URI the agent prints on the farm PC's console. Until it existed,
 * the device flow was complete on the server and unreachable in practice: the
 * agent displayed a code and the address it told the farmer to open served
 * nothing. `OAUTH_VERIFICATION_URI` points here (`https://hofsync.de/connect`
 * in production, `configmap.yaml` in dev), so the path is a deployed contract
 * — renaming this route breaks every already-installed agent, which is why it
 * is not derived from anything.
 *
 * Three steps, and the middle one is the security-relevant one: the farmer sees
 * WHAT is asking (the registry's `clients` allowlist entry) and WHICH farm it
 * will be bound to (their own session) BEFORE consenting. The agent never
 * chooses its tenant; it learns it as a result of this approval.
 */

type Phase = 'code' | 'confirm' | 'approved' | 'denied';

const ConnectPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  // RFC 8628's `verification_uri_complete` may prefill the code. Still shown in
  // the field rather than auto-approved: consent means the human read what they
  // are agreeing to, and a link that pairs on load is not consent.
  const [code, setCode] = useState(() => normaliseUserCode(searchParams.get('user_code') ?? ''));
  const [agentName, setAgentName] = useState('');
  const [phase, setPhase] = useState<Phase>('code');
  const [verification, setVerification] = useState<DeviceVerification | null>(null);
  const [approved, setApproved] = useState<ApprovedClient | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const failureMessage = useCallback(
    (err: unknown): string => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      // 404 covers unknown, already-used AND expired codes with one answer —
      // the server does that deliberately, so telling an attacker which short
      // codes were once real is not something this screen can leak either.
      if (status === 404) {
        return t(
          'connect.errors.notFound',
          'Dieser Code ist unbekannt oder abgelaufen. Bitte starte den Agenten neu und versuche es mit dem neuen Code.'
        );
      }
      if (status === 409) {
        return t('connect.errors.alreadyPaired', 'Dieser Hof ist bereits mit einem Agenten verbunden. Trenne den alten Agenten zuerst in den Einstellungen.');
      }
      return t('connect.errors.generic', 'Das hat nicht geklappt. Bitte versuche es erneut.');
    },
    [t]
  );

  const submitCode = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setError(null);
      setBusy(true);
      try {
        setVerification(await devicePairingService.verify(code));
        setPhase('confirm');
      } catch (err) {
        setError(failureMessage(err));
      } finally {
        setBusy(false);
      }
    },
    [code, failureMessage]
  );

  const approve = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      setApproved(await devicePairingService.approve(code, agentName.trim() || undefined));
      setPhase('approved');
    } catch (err) {
      setError(failureMessage(err));
    } finally {
      setBusy(false);
    }
  }, [code, agentName, failureMessage]);

  const deny = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      await devicePairingService.deny(code);
      setPhase('denied');
    } catch (err) {
      setError(failureMessage(err));
    } finally {
      setBusy(false);
    }
  }, [code, failureMessage]);

  return (
    <main className="connect-page" aria-labelledby="connect-title">
      <h1 id="connect-title" className="connect-page__title">
        {t('connect.title', 'Agent verbinden')}
      </h1>

      {error && (
        <p className="connect-page__error" role="alert">
          {error}
        </p>
      )}

      {phase === 'code' && (
        <form className="connect-page__form" onSubmit={submitCode}>
          <p className="connect-page__lead">
            {t('connect.lead', 'Gib den Code ein, den der Agent auf deinem Hof-PC anzeigt.')}
          </p>
          <label className="connect-page__label" htmlFor="connect-user-code">
            {t('connect.codeLabel', 'Code')}
          </label>
          <input
            id="connect-user-code"
            className="connect-page__code-input"
            value={code}
            // Normalised on every keystroke so the farmer sees the same
            // ABCD-EFGH grouping the agent printed, whether or not they typed
            // the dash. Mismatched formatting reads as a wrong code.
            onChange={(event) => setCode(normaliseUserCode(event.target.value))}
            placeholder="ABCD-EFGH"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={9}
          />
          <button className="connect-page__primary" type="submit" disabled={busy || code.length < 9}>
            {t('connect.continue', 'Weiter')}
          </button>
        </form>
      )}

      {phase === 'confirm' && verification && (
        <section className="connect-page__confirm" aria-labelledby="connect-confirm-title">
          <h2 id="connect-confirm-title" className="connect-page__subtitle">
            {t('connect.confirmTitle', 'Zugriff erlauben?')}
          </h2>
          <dl className="connect-page__facts">
            <div>
              <dt>{t('connect.software', 'Software')}</dt>
              {/* The registry `clients` allowlist entry. The caller is
                  unauthenticated when it starts a device authorization, so a
                  free-form name here would be a phishing field. */}
              <dd data-testid="connect-software-id">{verification.software_id}</dd>
            </div>
            <div>
              <dt>{t('connect.provider', 'Datenquelle')}</dt>
              <dd data-testid="connect-provider">{verification.provider}</dd>
            </div>
            <div>
              <dt>{t('connect.scope', 'Berechtigung')}</dt>
              <dd data-testid="connect-scope">{verification.scope}</dd>
            </div>
          </dl>

          <label className="connect-page__label" htmlFor="connect-agent-name">
            {t('connect.nameLabel', 'Name für diesen Agenten (optional)')}
          </label>
          <input
            id="connect-agent-name"
            className="connect-page__name-input"
            value={agentName}
            onChange={(event) => setAgentName(event.target.value)}
            placeholder={t('connect.namePlaceholder', 'z. B. Hof-PC')}
            maxLength={120}
          />

          <div className="connect-page__actions">
            <button className="connect-page__primary" type="button" onClick={approve} disabled={busy}>
              {t('connect.approve', 'Verbinden')}
            </button>
            <button className="connect-page__secondary" type="button" onClick={deny} disabled={busy}>
              {t('connect.deny', 'Ablehnen')}
            </button>
          </div>
        </section>
      )}

      {phase === 'approved' && (
        <section className="connect-page__done" role="status">
          <p className="connect-page__done-title">{t('connect.approvedTitle', 'Agent verbunden.')}</p>
          <p>
            {/* Says where to look next, because the agent's first sync is not
                instant and an empty settings page would otherwise read as a
                failed pairing. */}
            {t(
              'connect.approvedBody',
              'Der Agent kann jetzt Daten übertragen. Den Status siehst du in den Einstellungen unter Integrationen.'
            )}
          </p>
          {approved?.name && <p className="connect-page__done-name">{approved.name}</p>}
        </section>
      )}

      {phase === 'denied' && (
        <section className="connect-page__done" role="status">
          <p className="connect-page__done-title">{t('connect.deniedTitle', 'Zugriff abgelehnt.')}</p>
          <p>{t('connect.deniedBody', 'Es wurde nichts verbunden.')}</p>
        </section>
      )}
    </main>
  );
};

export default ConnectPage;
